"use client"
import type React from "react"
import { useState, useMemo, useCallback, useEffect } from "react"
import { Card, CardBody, Form, FormGroup, Label, Input, Button, Alert, Row, Col, Spinner } from "reactstrap"
import type { TicketPricingResponse, TicketPurchaseFormData, FormErrors, PaymentResponse } from "./ticket-purchase"
import TicketService from "../../../Service/purchaseTicketService"

const TicketPurchaseForm: React.FC = () => {
  const [pricingData, setPricingData] = useState<TicketPricingResponse | null>(null)
  const [formData, setFormData] = useState<TicketPurchaseFormData>({
    phoneNumber: "",
    numTickets: 1,
    language: "ENG",
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [paymentResponse, setPaymentResponse] = useState<PaymentResponse | null>(null)

  useEffect(() => {
    fetchPricingData()
  }, [])

  const fetchPricingData = async () => {
    try {
      setLoading(true)
      const data = await TicketService.getTicketPricing()
      setPricingData(data)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load ticket information"
      setErrors({ general: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const totalAmount = useMemo(() => {
    if (!pricingData) return 0
    return TicketService.calculateTotal(pricingData.pricing.ticket_price, formData.numTickets)
  }, [pricingData, formData.numTickets])

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required"
    } else if (!TicketService.validatePhoneNumber(formData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid number (078, 079, 072, or 073)"
    }

    if (formData.numTickets < 1 || formData.numTickets > 5) {
      newErrors.numTickets = "Number of tickets must be between 1 and 5"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Memoized phone change handler to prevent re-renders
  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setFormData((prev) => ({ ...prev, phoneNumber: value }))

      // Clear phone error only, don't clear all errors
      if (errors.phoneNumber) {
        setErrors((prev) => ({ ...prev, phoneNumber: undefined }))
      }
    },
    [errors.phoneNumber],
  )

  const handleInputChange = (field: keyof TicketPurchaseFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) {
      return
    }

    try {
      setSubmitting(true)
      setErrors({})
      setPaymentResponse(null)
      const paymentRequest = {
        phone_number: formData.phoneNumber.replace(/\s/g, ""),
        num_tickets: formData.numTickets,
        language: formData.language,
      }

      const response = await TicketService.submitTicketPayment(paymentRequest)
      setPaymentResponse(response)

      setFormData({
        phoneNumber: "",
        numTickets: 1,
        language: "ENG",
      })
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Payment processing failed"
      setErrors({ general: errorMessage })
    } finally {
      setSubmitting(false)
    }
  }

  const handleNewRequest = () => {
    setPaymentResponse(null)
    setErrors({})
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <CardBody className="text-center py-4">
            <Spinner color="primary" className="mb-3" />
            <p className="text-muted">Loading ticket information...</p>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (!pricingData) {
    return (
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <CardBody className="text-center py-4">
            <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h5 className="text-warning">Unable to Load Ticket Information</h5>
            <p className="text-muted">{errors.general || "Please try again later"}</p>
            <Button color="primary" onClick={fetchPricingData}>
              <i className="fa fa-refresh me-2"></i>
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  // Show success response
  if (paymentResponse) {
    return (
      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <CardBody
            className="text-center bg-primary p-3"
            style={{
            //   background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
              color: "white",
              borderRadius: "0.5rem",
            }}

          >
            <div
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                background: "rgba(89, 217, 25, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5rem",
                margin: "0 auto 1rem",
              }}
            >
              <i className="fa fa-check"></i>
            </div>
            <h5 className="fw-bold mb-2">Payment Initiated Successfully!</h5>
            <p className="mb-3">{paymentResponse.instructions}</p>
            <div
              style={{
                background: "rgba(255,255,255,0.1)",
                borderRadius: "0.3rem",
                padding: "1rem",
                margin: "1rem 0",
                fontSize: "0.9rem",
              }}

              
            >
              <div className="d-flex justify-content-between mb-2">
                <span>Exhibition:</span>
                <strong>{paymentResponse.details.exhibition_title}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Phone:</span>
                <strong>{TicketService.formatPhoneNumber(paymentResponse.details.phone_number)}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Tickets:</span>
                <strong>{paymentResponse.details.num_tickets}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span>Total:</span>
                <strong className="fs-5">
                  {TicketService.formatCurrency(paymentResponse.details.total_amount, paymentResponse.details.currency)}
                </strong>
              </div>
            </div>
            <Button color="secondary" onClick={handleNewRequest} className="mt-2">
              <i className="fa fa-plus me-2"></i>
              New Purchase
            </Button>
          </CardBody>
        </Card>
      </div>
    )
  }

  return (
    <>
      <style>{`
        .compact-form {
          font-size: 1rem;
        }
        
        .compact-form .form-group {
          margin-bottom: 0.75rem;
        }
        
        .compact-form .form-control {
          font-size: 0.85rem;
          padding: 0.4rem 0.6rem;
        }
        
        .compact-form label {
          font-size: 0.8rem;
          margin-bottom: 0.3rem;
          font-weight: 600;
        }
        
        .compact-form small {
          font-size: 0.7rem;
        }
        
        .compact-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1rem;
          text-align: center;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
        }
        
        .compact-header h5 {
          font-size: 1.1rem;
          margin-bottom: 0.3rem;
        }
        
        .compact-header p {
          font-size: 0.8rem;
          margin-bottom: 0.5rem;
        }
        
        .compact-price {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 0.3rem;
          padding: 0.5rem;
          font-size: 0.85rem;
        }
        
        .compact-counter {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }
        
        .compact-counter-btn {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #667eea;
          background: white;
          color: #667eea;
          font-weight: bold;
          cursor: pointer;
          font-size: 1rem;
        }
        
        .compact-counter-btn:hover {
          background: #667eea;
          color: white;
        }
        
        .compact-counter-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .compact-total {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          padding: 1rem;
          border-radius: 0.5rem;
          text-align: center;
          margin: 1rem 0;
        }
        
        .compact-breakdown {
          background: rgba(255,255,255,0.1);
          border-radius: 0.3rem;
          padding: 0.6rem;
          margin-top: 0.6rem;
          font-size: 0.8rem;
        }
        
        .compact-submit {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 0.7rem 1.5rem;
          border-radius: 0.5rem;
          font-weight: bold;
          width: 100%;
          font-size: 0.9rem;
        }
        
        .compact-radio {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }
        
        .compact-radio label {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.8rem;
          margin-bottom: 0;
          cursor: pointer;
        }
        
        .compact-radio input[type="radio"] {
          width: 15px;
          height: 15px;
        }

        .phone-language-row {
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }

        .phone-field {
          flex: 1;
        }

        .language-field {
          flex: 0 0 auto;
          min-width: 180px;
        }

        @media (max-width: 576px) {
          .phone-language-row {
            flex-direction: column;
            gap: 0.75rem;
          }
          
          .language-field {
            min-width: auto;
          }
          
          .compact-form {
            font-size: 0.8rem;
          }
          
          .compact-form .form-control {
            font-size: 0.8rem;
            padding: 0.35rem 0.5rem;
          }
          
          .compact-counter {
            gap: 0.8rem;
          }
          
          .compact-counter-btn {
            width: 30px;
            height: 30px;
            font-size: 0.9rem;
          }
          
          .compact-header {
            padding: 0.8rem;
          }
          
          .compact-header h5 {
            font-size: 1rem;
          }
        }
      `}</style>

      <div className="d-flex justify-content-center">
        <Card style={{ maxWidth: "500px", width: "100%" }}>
          <CardBody className="compact-form">
            {/* Exhibition Header */}
            <div className="compact-header">
              <h5 className="fw-bold">{pricingData.exhibition.title}</h5>
              <p className="opacity-90 mb-0">
                <i className="fa fa-map-marker me-2"></i>
                {pricingData.exhibition.venue}
              </p>
              <div className="compact-price">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Ticket Price:</span>
                  <strong>
                    {TicketService.formatCurrency(pricingData.pricing.ticket_price, pricingData.pricing.currency)}
                  </strong>
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {errors.general && (
              <Alert color="danger" className="py-2 px-3 mb-3">
                <i className="fa fa-exclamation-triangle me-2"></i>
                {errors.general}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              {/* Phone Number and Language on Same Line */}
              <div className="phone-language-row">
                {/* Phone Number */}
                <div className="phone-field">
                  <FormGroup>
                    <Label htmlFor="phoneNumber">
                      <i className="fa fa-phone me-2"></i>
                      Phone Number *
                    </Label>
                    <Input
                      key="phone-input"
                      type="number"
                      id="phoneNumber"
                      name="phoneNumber"
                      placeholder="078 123 456"
                      value={formData.phoneNumber}
                      onChange={handlePhoneChange}
                      className={errors.phoneNumber ? "is-invalid" : ""}
                      autoComplete="off"
                    />
                    {errors.phoneNumber && (
                      <div className="text-danger mt-1">
                        <small>{errors.phoneNumber}</small>
                      </div>
                    )}
                    <small className="text-muted">Enter: 078, 079, 072, or 073</small>
                  </FormGroup>
                </div>

                {/* Language Selection */}
                <div className="language-field">
                  <FormGroup>
                    <Label>
                      <i className="fa fa-language me-2"></i>
                      Language *
                    </Label>
                    <div className="compact-radio">
                      <label>
                        <input
                          type="radio"
                          name="language"
                          value="ENG"
                          checked={formData.language === "ENG"}
                          onChange={(e) => handleInputChange("language", e.target.value)}
                        />
                        English
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="language"
                          value="KNY"
                          checked={formData.language === "KNY"}
                          onChange={(e) => handleInputChange("language", e.target.value)}
                        />
                        Kinyarwanda
                      </label>
                    </div>
                  </FormGroup>
                </div>
              </div>

              {/* Number of Tickets */}
              <FormGroup>
                <Label>
                  <i className="fa fa-ticket me-2"></i>
                  Number of Tickets *
                </Label>
                <div className="compact-counter">
                  <button
                    type="button"
                    className="compact-counter-btn"
                    onClick={() => handleInputChange("numTickets", Math.max(1, formData.numTickets - 1))}
                    disabled={formData.numTickets <= 1}
                  >
                    -
                  </button>
                  <div className="text-center">
                    <div className="fs-2 fw-bold text-primary">{formData.numTickets}</div>
                    <small className="text-muted">{formData.numTickets === 1 ? "Ticket" : "Tickets"}</small>
                  </div>
                  <button
                    type="button"
                    className="compact-counter-btn"
                    onClick={() => handleInputChange("numTickets", Math.min(5, formData.numTickets + 1))}
                    disabled={formData.numTickets >= 5}
                  >
                    +
                  </button>
                </div>
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Maximum {pricingData.pricing.max_tickets_per_purchase} tickets per purchase
                  </small>
                </div>
              </FormGroup>

              {/* Total Amount */}
              <div className="compact-total">
               
                <div className="compact-breakdown">
                  <div className="d-flex justify-content-between">
                    <span>Price per ticket:</span>
                    <span>
                      {TicketService.formatCurrency(pricingData.pricing.ticket_price, pricingData.pricing.currency)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Number of tickets:</span>
                    <span>{formData.numTickets}</span>
                  </div>
                  <hr className="my-2 opacity-50" />
                  <div className="d-flex justify-content-between fw-bold">
                    <span>Total:</span>
                    <span>{TicketService.formatCurrency(totalAmount, pricingData.pricing.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button type="submit" className="compact-submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <i className="fa fa-paper-plane me-2"></i>
                    Submit Payment Request
                  </>
                )}
              </Button>
              <div className="text-center mt-2">
                <small className="text-muted">
                  <i className="fa fa-info-circle me-1"></i>
                  This will send payment request to customer's phone
                </small>
              </div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </>
  )
}

export default TicketPurchaseForm
